import { FILE_UPLOAD_LIMIT_IN_BYTES } from '@/lib/constants';
import { formatFileSize } from '@/lib/formatFileSize';
import { FolderPlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ChangeEvent, DragEvent, useState } from 'react';
import Button, { ButtonVariants } from '../../atoms/legacy/Button';
import PopOutModal from '../../molecules/legacy/PopOutModal/PopOutModal';

interface BaseModalProps {
  open: boolean;
  close: () => void;
  onUploadLicense: (license: File) => void;
  clearUploadedLicense: () => void;
  uploadedLicense: File | null;
}

const FILE_TYPES_TO_DISPLAY = ['application/pdf', 'image/png', 'image/jpg'];
const ALLOWED_FILE_TYPES = [...FILE_TYPES_TO_DISPLAY, 'image/jpeg'];

export const UploadDealerLicenseModal: React.FC<BaseModalProps> = ({
  open,
  close,
  onUploadLicense,
  clearUploadedLicense,
  uploadedLicense,
}) => {
  const [errorUploading, setErrorUploading] = useState('');

  const handleUploadedFile = (files: FileList) => {
    const [uploadedFile] = Array.from(files).filter((droppedFile) =>
      ALLOWED_FILE_TYPES.includes(droppedFile.type)
    );

    if (!uploadedFile) {
      setErrorUploading(
        'There was a problem uploading this file. Please try again.'
      );
    } else if (
      !!uploadedFile &&
      uploadedFile.size >= FILE_UPLOAD_LIMIT_IN_BYTES
    ) {
      setErrorUploading(
        'File size is too large. Please upload a file under 8 MB.'
      );
    } else {
      onUploadLicense(uploadedFile);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    setErrorUploading('');
    e.preventDefault();

    handleUploadedFile(e.dataTransfer.files);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    setErrorUploading('');
    if (e.target?.files) {
      handleUploadedFile(e.target.files);
    }
  };

  const onInputClick = (
    event: React.MouseEvent<HTMLInputElement, MouseEvent>
  ) => {
    const element = event.target as HTMLInputElement;
    element.value = '';
  };

  const renderPreview = () => {
    if (uploadedLicense) {
      if (uploadedLicense.type.startsWith('image/')) {
        return (
          <img
            src={URL.createObjectURL(uploadedLicense)}
            alt="Preview"
            className="h-[300px] w-full"
            width="100%"
          />
        );
      }
      if (uploadedLicense.type === 'application/pdf') {
        return (
          <embed
            src={URL.createObjectURL(uploadedLicense)}
            className="h-[600px] w-full"
            type={uploadedLicense.type}
            width="100%"
            height="600px"
          />
        );
      }
    }
    return null;
  };

  return (
    <PopOutModal open={open} onClose={close}>
      <div>
        <div className="mb-[24px]">
          <span className="text-display32px">Upload dealer license</span>
        </div>
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="mb-[24px] flex cursor-pointer flex-col items-center justify-center border-[1px] border-dashed border-neutral-300 bg-neutral-100 p-2 text-center lg:p-6"
          tabIndex={0}
          role="button"
          aria-label="File Upload"
        >
          <input
            id="fileInput"
            type="file"
            accept={ALLOWED_FILE_TYPES.join(',')}
            onChange={handleFileChange}
            onClick={onInputClick}
            style={{ display: 'none' }}
            tabIndex={-1}
          />
          {!uploadedLicense && (
            <>
              <div className="mb-[24px] flex h-[32px] w-[32px] items-center justify-center rounded-[50px] bg-teal-300 bg-opacity-[.15]">
                <FolderPlusIcon className="size-l text-brandElectric" />
              </div>
              <label
                htmlFor="fileInput"
                className="mb-[8px] text-body16px font-normal"
              >
                Drag & drop a file here or{' '}
                <span
                  role="button"
                  className="cursor-pointer font-medium underline decoration-brandElectric underline-offset-4"
                >
                  Browse File
                </span>
              </label>
              <span className="text-subtitle14px text-neutral-600">
                Support:{' '}
                {FILE_TYPES_TO_DISPLAY.map((fileName) =>
                  fileName.split('/')[1].toUpperCase()
                ).join(', ')}{' '}
                • Up to 8 MB
              </span>
            </>
          )}

          {!!uploadedLicense && renderPreview()}
        </div>
        {!!uploadedLicense && (
          <div className="items-star6 mb-4 flex flex-col">
            <div className="flex w-full flex-row items-center justify-between">
              <div>
                <span className="text-subtitle-16px">
                  {uploadedLicense.name}
                </span>
              </div>
              <div role="button" onClick={clearUploadedLicense}>
                <XMarkIcon className="size-[20px] stroke-[2.5px] text-black" />
              </div>
            </div>
            <span className="text-subtitle14px text-neutral-600">
              {formatFileSize(uploadedLicense.size)}
            </span>
          </div>
        )}

        <div>
          {!!errorUploading && (
            <div className="mb-2">
              <span className="text-subtitle-16px text-red-medium">
                {errorUploading}
              </span>
            </div>
          )}
          <div className="flex flex-row justify-between gap-[8px]">
            <Button
              variant={ButtonVariants.newDefault}
              onClick={() => {
                clearUploadedLicense();
                close();
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant={ButtonVariants.newPrimary}
              disabled={!uploadedLicense || !!errorUploading}
              onClick={close}
              className="flex-1"
            >
              Submit
            </Button>
          </div>
        </div>
      </div>
    </PopOutModal>
  );
};
